import api from '@/helpers/api'

const API_TENANTS = '/api/extranet/corporate' // Using corporate endpoint to get Corporate UUIDs + Names
const API_DASHBOARD = '/api/extranet/dashboard'

export default {
  namespaced: true,

  state: {
    selectedTenant: null,           // { id, name, domain, code, status, tenant_id }
    selectedTenantDomain: null,     // 'jeevawasa.localhost'
    tenants: [],                    // List of all tenants (corporates)
    dashboardData: null,            // Dashboard metrics (global aggregated)
    tenantStats: null,              // Stats for currently selected tenant
    loading: false,
    error: null
  },

  mutations: {
    SET_TENANT(state, { tenant, domain }) {
      state.selectedTenant = tenant
      state.selectedTenantDomain = domain
      // Persist to localStorage
      localStorage.setItem('extranet_selected_tenant', JSON.stringify({ tenant, domain }))

      // Also update Auth store to mock business profile for reused components
      // This is crucial for Branch.vue/Merchant.vue to filter by corporate_id
      const businessProfile = {
        corporate_id: tenant.id, // Corporate UUID
        branch_id: null,
        merchant_id: null
      }
      localStorage.setItem('selected_business_profile', JSON.stringify(businessProfile))
    },

    CLEAR_TENANT(state) {
      state.selectedTenant = null
      state.selectedTenantDomain = null
      state.tenantStats = null
      localStorage.removeItem('extranet_selected_tenant')
      localStorage.removeItem('selected_business_profile')
    },

    SET_TENANTS(state, tenants) {
      state.tenants = tenants
    },

    SET_DASHBOARD(state, data) {
      state.dashboardData = data
    },

    SET_TENANT_STATS(state, stats) {
      state.tenantStats = stats
    },

    SET_LOADING(state, loading) {
      state.loading = loading
    },

    SET_ERROR(state, error) {
      state.error = error
    }
  },

  actions: {
    /**
     * Fetch list of all tenants (actually corporates)
     * Used in tenant selector dropdown
     *
     * @param {Object} payload - Optional { search: string }
     */
    async fetchTenants({ commit }, payload = {}) {
      commit('SET_LOADING', true)
      try {
        const params = {
          custom: {
            per_page: 100 // Fetch up to 100 tenants
          }
        }

        // Add search keyword if provided (server-side search by name)
        if (payload.search) {
          params.custom.name = payload.search
        }

        const response = await api.request('get', API_TENANTS, params)

        // API returns { data: { data: [...], meta: ... } }
        // We map fields for the selector
        const tenants = response.data.data.data.map(corp => ({
          id: corp.id, // Corporate UUID
          name: corp.name, // Corporate Name
          domain: corp.tenant_domain, // Tenant Subdomain
          tenant_id: corp.tenant_id, // Tenant UUID
          code: corp.code,
          status: corp.register_option?.status === 1 ? 'active' : 'inactive'
        }))

        commit('SET_TENANTS', tenants)
        commit('SET_ERROR', null)
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch tenants'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Fetch dashboard data with cross-tenant aggregation
     * No X-Tenant-Domain header needed (Central DB)
     */
    async fetchDashboard({ commit }) {
      commit('SET_LOADING', true)
      try {
        const response = await api.request('get', API_DASHBOARD)
        // Extract data from response.data.data (API returns { status, message, data })
        commit('SET_DASHBOARD', response.data.data)
        commit('SET_ERROR', null)
      } catch (error) {
        const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch dashboard data'
        commit('SET_ERROR', errorMessage)
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Fetch tenant-specific statistics (members, branches, merchants)
     * 
     * Calls GET /api/extranet/corporate/{corporateId}/stats?tenant_id={tenantId}
     * Uses extranet.auth — no X-Tenant-Domain header required.
     * Errors are caught silently so dashboard still renders even if stats fail.
     * 
     * @param {Object} payload - { corporateId, tenantId }
     */
    async fetchTenantStats({ commit }, { corporateId, tenantId }) {
      if (!corporateId || !tenantId) {
        console.warn('[extranet] fetchTenantStats called without corporateId or tenantId')
        return
      }

      try {
        // Use { custom: { key: value } } format — api.js builds ?key=value from params.custom
        const response = await api.request(
          'get',
          `/api/extranet/corporate/${corporateId}/stats`,
          { custom: { tenant_id: tenantId } }
        )
        commit('SET_TENANT_STATS', response.data.data)
      } catch (error) {
        // Non-blocking: log the error but don't rethrow
        // Dashboard still renders with 0 values rather than crashing
        console.error('[extranet] fetchTenantStats failed:', error.response?.data?.message || error.message)
        commit('SET_TENANT_STATS', null)
      }
    },

    /**
     * Select a tenant for management
     * Persists selection to localStorage
     * 
     * @param {Object} payload - { id, name, domain, tenant_id, code, status }
     */
    selectTenant({ commit }, payload) {
      // payload is the tenant object from fetchTenants map
      // Use domain (tenant_domain) for X-Tenant-Domain header
      commit('SET_TENANT', { tenant: payload, domain: payload.domain })

      // Also commit to Auth store for runtime state
      const businessProfile = {
        corporate_id: payload.id, // Corporate UUID
        branch_id: null,
        merchant_id: null
      }
      commit('Auth/select_business_profile', { data: businessProfile }, { root: true })
    },

    /**
     * Clear tenant selection
     * Removes from localStorage
     */
    clearTenant({ commit }) {
      commit('CLEAR_TENANT')
      // Also clear in Auth store
      commit('Auth/select_business_profile', { data: { corporate_id: '', branch_id: '', merchant_id: '' } }, { root: true })
    },

    /**
     * Restore tenant selection from localStorage
     * Called on app initialization
     */
    restoreTenantSelection({ commit }) {
      const saved = localStorage.getItem('extranet_selected_tenant')
      if (saved) {
        try {
          const { tenant, domain } = JSON.parse(saved)
          commit('SET_TENANT', { tenant, domain })

          // Auth store typically restores from localStorage on init, no need to force commit unless needed
          // But committing ensures syncing if Auth store logic changes
          const businessProfile = {
            corporate_id: tenant.id,
            branch_id: null,
            merchant_id: null
          }
          commit('Auth/select_business_profile', { data: businessProfile }, { root: true })
        } catch (error) {
          // Invalid JSON in localStorage, clear it
          localStorage.removeItem('extranet_selected_tenant')
        }
      }
    }
  },

  getters: {
    /**
     * Check if a tenant is currently selected
     */
    hasTenantSelected: state => !!state.selectedTenantDomain,

    /**
     * Get current tenant name or default value
     */
    currentTenantName: state => state.selectedTenant?.name || '-',

    /**
     * Check if store is in loading state
     */
    isLoading: state => state.loading,

    /**
     * Check if there's an error
     */
    hasError: state => !!state.error
  }
}
